import { useEffect, useState, Children } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";

/**
 * Lightweight swipeable carousel for the hero.
 * - Auto-rotates every 7s
 * - Manual dots + left/right buttons
 * - Touch swipe support
 */
export function HeroCarousel({ children, autoRotateMs = 7000 }) {
  const slides = Children.toArray(children).filter(Boolean);
  const [idx, setIdx] = useState(0);
  const count = slides.length;

  useEffect(() => {
    if (count <= 1) return;
    const t = setInterval(() => setIdx((i) => (i + 1) % count), autoRotateMs);
    return () => clearInterval(t);
  }, [count, autoRotateMs]);

  // Touch swipe
  const [touchStart, setTouchStart] = useState(null);
  const onTouchStart = (e) => setTouchStart(e.touches[0].clientX);
  const onTouchEnd = (e) => {
    if (touchStart == null) return;
    const delta = e.changedTouches[0].clientX - touchStart;
    if (Math.abs(delta) > 40) {
      setIdx((i) => (delta < 0 ? (i + 1) % count : (i - 1 + count) % count));
    }
    setTouchStart(null);
  };

  if (count === 0) return null;

  return (
    <div
      className="mx-4 mt-2 relative"
      data-testid="hero-carousel"
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
    >
      <div className="relative aspect-[4/5] sm:aspect-[16/10] overflow-hidden rounded-2xl">
        {slides.map((slide, i) => (
          <div
            key={i}
            className={`absolute inset-0 transition-opacity duration-500 ${
              i === idx ? "opacity-100 z-10" : "opacity-0 z-0 pointer-events-none"
            }`}
          >
            {slide}
          </div>
        ))}
      </div>

      {count > 1 && (
        <>
          <button
            onClick={() => setIdx((i) => (i - 1 + count) % count)}
            data-testid="hero-prev"
            aria-label="Previous slide"
            className="hidden sm:grid place-items-center absolute left-2 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-black/55 backdrop-blur text-white hover:bg-black/75 transition z-20"
          >
            <ChevronLeft size={18} />
          </button>
          <button
            onClick={() => setIdx((i) => (i + 1) % count)}
            data-testid="hero-next"
            aria-label="Next slide"
            className="hidden sm:grid place-items-center absolute right-2 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-black/55 backdrop-blur text-white hover:bg-black/75 transition z-20"
          >
            <ChevronRight size={18} />
          </button>

          <div className="absolute bottom-3 left-1/2 -translate-x-1/2 z-20 flex gap-1.5">
            {slides.map((_, i) => (
              <button
                key={i}
                onClick={() => setIdx(i)}
                data-testid={`hero-dot-${i}`}
                aria-label={`Go to slide ${i + 1}`}
                className={`h-1.5 rounded-full transition-all ${
                  i === idx ? "w-6 bg-white" : "w-1.5 bg-white/40 hover:bg-white/70"
                }`}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
}
